import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bike, Package, CheckCircle2, Loader2, RefreshCw, LogOut } from "lucide-react";
import useEstafetaDashboard from "@/hooks/useEstafetaDashboard";
import StatsPanel from "@/components/estafeta/StatsPanel";
import OrderCard from "@/components/estafeta/OrderCard";
import LangToggle from "@/components/estafeta/LangToggle";

export default function EstafetaDashboard({ deliverer: initialDeliverer, token, onLogout, lang, setLang, t }) {
  const {
    deliverer, assigned, completed, loading, actionLoading,
    refreshing, gpsActive, gpsError, gpsPermission, lastLocationSent,
    handleRequestGps, handleRefresh, handlePickup, handleDeliver,
    handleArrived, handleLogout,
  } = useEstafetaDashboard({ initialDeliverer, token, onLogout, t });

  const [tab, setTab] = useState("active");

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  const _now = Date.now();
  const _minsAgo = (d) => d ? Math.floor((_now - new Date(d).getTime()) / 60000) : Infinity;
  const _statusPriority = (s) => s === "delivering" ? 0 : s === "ready" ? 1 : 2;
  const _hasAlert = (o) => {
    const dMin = _minsAgo(o.dispatched_at);
    const pMin = _minsAgo(o.picked_up_at);
    return (o.status === "ready" && !!o.deliverer_id && dMin >= 10)
        || (o.status === "delivering" && pMin < Infinity && pMin >= 25);
  };
  const sortedOrders = [...assigned].sort((a, b) => {
    const sp = _statusPriority(a.status) - _statusPriority(b.status);
    if (sp !== 0) return sp;
    const alertDiff = (_hasAlert(b) ? 1 : 0) - (_hasAlert(a) ? 1 : 0);
    if (alertDiff !== 0) return alertDiff;
    return new Date(a.dispatched_at || a.created_at) - new Date(b.dispatched_at || b.created_at);
  });

  const activeCount = assigned.length;
  console.log("[ESTAFETA_RENDER_ASSIGNED]", {
    component: "EstafetaDashboard",
    tab,
    assigned_count: assigned.length,
    completed_count: completed.length,
    assigned_statuses: assigned.map(order => order.status),
  });
  if (tab === "active" && assigned.length === 0) {
    console.log("[ESTAFETA_EMPTY_STATE_REASON]", {
      component: "EstafetaDashboard",
      reason: "active_tab_assigned_array_empty",
      assigned_count: assigned.length,
      completed_count: completed.length,
      loading,
    });
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="estafeta-dashboard">
      {/* Header */}
      <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
            <Bike className="w-5 h-5" />
          </div>
          <div>
            <p className="font-bold text-sm leading-tight">{deliverer.name}</p>
            <p className="text-emerald-200 text-xs">{deliverer.vehicle === "moto" ? t("moto") : deliverer.vehicle === "bicycle" ? t("bicycle") : t("car")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${deliverer.status === "delivering" ? "bg-purple-500" : "bg-emerald-400"} text-white text-xs border-0`}>
            {deliverer.status === "delivering" ? t("inRoute") : t("available")}
          </Badge>
          <Badge
            className={`text-xs border-0 ${gpsActive ? "bg-white/20 text-white" : "bg-red-500 text-white"}`}
            data-testid="gps-status-badge"
          >
            {gpsActive ? "📍 GPS" : "⚠️ GPS"}
          </Badge>
          <LangToggle lang={lang} setLang={setLang} />
          <button onClick={handleLogout} className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center" data-testid="estafeta-logout">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* GPS status banner */}
      {gpsActive ? (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-1.5 flex items-center gap-2" data-testid="gps-active-banner">
          <span className="text-emerald-600 text-xs font-medium">📍 GPS ativo</span>
          {lastLocationSent && (
            <span className="text-emerald-500 text-[11px]">
              · última atualização: {new Date(lastLocationSent).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
        </div>
      ) : (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2.5" data-testid="gps-warning-banner">
          <div className="flex items-start gap-2">
            <span className="text-red-500 text-base leading-none mt-0.5">⚠️</span>
            <div className="flex-1">
              <p className="text-red-700 text-xs font-semibold leading-tight">
                GPS inativo — o cliente não consegue acompanhar a entrega
              </p>
              <p className="text-red-500 text-[11px] mt-0.5 leading-tight">
                {gpsError || "Ative a localização do telemóvel para que o cliente acompanhe a entrega em tempo real."}
              </p>
              {gpsPermission && (
                <p className="text-red-400 text-[11px] mt-0.5">
                  Permissão GPS: {gpsPermission === "granted" ? "concedida" : gpsPermission === "denied" ? "negada — ative nas definições do navegador" : "pendente"}
                </p>
              )}
              {gpsPermission !== "denied" && (
                <button
                  onClick={handleRequestGps}
                  className="mt-1.5 text-[11px] font-semibold text-white bg-red-500 hover:bg-red-600 rounded px-2 py-0.5"
                  data-testid="activate-gps-btn"
                >
                  Ativar localização
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats Panel */}
      <StatsPanel token={token} deliverer={deliverer} t={t} />

      {/* Tabs */}
      <div className="bg-white border-b flex sticky top-[52px] z-40">
        <button
          className={`flex-1 py-3 text-sm font-bold text-center transition-all border-b-2 ${tab === "active" ? "text-emerald-700 border-emerald-600" : "text-gray-400 border-transparent"}`}
          onClick={() => setTab("active")}
          data-testid="tab-active"
        >
          PEDIDOS ({activeCount})
        </button>
        <button
          className={`flex-1 py-3 text-sm font-bold text-center transition-all border-b-2 ${tab === "completed" ? "text-emerald-700 border-emerald-600" : "text-gray-400 border-transparent"}`}
          onClick={() => setTab("completed")}
          data-testid="tab-completed"
        >
          {t("history")} ({completed.length})
        </button>
        <button
          onClick={handleRefresh}
          className="px-3 text-gray-400 hover:text-emerald-600 transition-colors"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 space-y-3 pb-20">
        {tab === "active" && (
          <>
            {assigned.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">{t("noOrders")}</p>
                <p className="text-gray-400 text-sm mt-1">{t("ordersWillAppear")}</p>
                <Button variant="outline" size="sm" className="mt-4 gap-2" onClick={handleRefresh} data-testid="refresh-empty-btn">
                  <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> {t("refresh")}
                </Button>
              </div>
            ) : (
              sortedOrders.map(order => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onPickup={handlePickup}
                  onDeliver={handleDeliver}
                  onArrived={handleArrived}
                  loading={actionLoading === order.id}
                  token={token}
                  t={t}
                />
              ))
            )}
          </>
        )}
        {tab === "completed" && (
          <>
            {completed.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">{t("noDeliveries")}</p>
              </div>
            ) : (
              completed.map(order => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onPickup={handlePickup}
                  onDeliver={handleDeliver}
                  onArrived={handleArrived}
                  loading={false}
                  token={token}
                  t={t}
                />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}
